import React, {
  FC,
  useEffect,
  useRef,
  useState,
} from 'react';
import cx from 'classnames';
import { apiClient, _t, _t_back } from 'utils';
import type { BaseFilter } from 'types';

import iconCaret from 'assets/img/resolutions-v4/icon-caret-down.svg';

import {
  LIBRARY_FILTER_DEFS,
  LibraryFilterDef,
  LibraryFilterKey,
  LibraryFilterState,
} from '../shared';
import styles from './styles.module.scss';

type Props = {
  value: LibraryFilterState,
  onChange: (next: LibraryFilterState) => void,
  className?: string,
};

type OptionsMap = Partial<Record<LibraryFilterKey, BaseFilter[]>>;

const optionLabel = (item: BaseFilter): string => (
  String(_t_back(item, 'title') || item.title || item.short_description || item.name || '')
);

const LibraryFilters: FC<Props> = ({
  value,
  onChange,
  className,
}) => {
  const [openKey, setOpenKey] = useState<LibraryFilterKey | null>(null);
  const [options, setOptions] = useState<OptionsMap>({});
  const rootRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    let cancelled = false;

    const loadAll = async () => {
      const entries = await Promise.all(
        LIBRARY_FILTER_DEFS.map(async (def) => {
          try {
            const res = await apiClient({
              method: 'get',
              url: def.url,
            });
            const list = Array.isArray(res.data) ? res.data : [];
            return [def.key, list] as const;
          } catch (error) {
            return [def.key, [] as BaseFilter[]] as const;
          }
        }),
      );

      if (!cancelled) {
        setOptions(Object.fromEntries(entries));
      }
    };

    loadAll();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onDocClick = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpenKey(null);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const toggleOption = (def: LibraryFilterDef, optionId: number) => {
    const current = value[def.key] || [];
    let nextSelected: number[];

    if (def.mode === 'single') {
      nextSelected = current.includes(optionId) ? [] : [optionId];
      setOpenKey(null);
    } else if (current.includes(optionId)) {
      nextSelected = current.filter((id) => id !== optionId);
    } else {
      nextSelected = [...current, optionId];
    }

    onChange({
      ...value,
      [def.key]: nextSelected,
    });
  };

  return (
    <ul className={cx(styles.filters, className)} ref={rootRef}>
      {LIBRARY_FILTER_DEFS.map((def) => {
        const selected = value[def.key] || [];
        const list = options[def.key] || [];
        const isOpen = openKey === def.key;
        const active = selected.length > 0;

        return (
          <li key={def.key} className={styles.filterItem}>
            <button
              type="button"
              className={cx(styles.filterChip, active && styles.filterChipActive)}
              aria-expanded={isOpen}
              aria-haspopup="listbox"
              onClick={() => setOpenKey(isOpen ? null : def.key)}
            >
              <span>{_t(def.label)}</span>
              <img src={iconCaret} alt="" className={styles.filterCaret} width={16} height={16} />
            </button>

            {isOpen ? (
              <ul className={styles.filterMenu} role="listbox">
                {list.length ? list.map((option) => {
                  const selectedOption = selected.includes(option.id);
                  return (
                    <li key={option.id}>
                      <button
                        type="button"
                        className={selectedOption ? styles.filterOptionActive : styles.filterOption}
                        role="option"
                        aria-selected={selectedOption}
                        onClick={() => toggleOption(def, option.id)}
                      >
                        {optionLabel(option)}
                      </button>
                    </li>
                  );
                }) : (
                  <li className={styles.filterEmpty}>{_t('Нет вариантов')}</li>
                )}
              </ul>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
};

export default LibraryFilters;
